import { Avatar, Card, CardContent, Stack, Typography } from '@mui/material'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { Button, Icons, InformationModal, ItemImageArea } from 'utoniq-ui'

import {
  ItemDescriptionFragment,
  Maybe,
  UserItemSerialDetailFragment,
} from '@/clients/utoniq-core/schema'
import { useCountdown } from '@/components/lib/react-hooks/useCountDown'
import { useMessage, useTranslation } from '@/core'
import { Organism } from '@/core/architecture'

type QRCodeBoxProps = {
  loading: boolean
  item?: Maybe<ItemDescriptionFragment>
  serial: UserItemSerialDetailFragment
  onGenerateWallpaperPrintQrcode: (serialId: string) => Promise<void>
}

export const QRCodeBox: Organism<QRCodeBoxProps> = ({
  loading,
  item,
  serial,
  onGenerateWallpaperPrintQrcode,
}) => {
  const { t } = useTranslation('ownedItemDetail')
  const { showError } = useMessage()
  const [openGenerateQRConfirm, setOpenGenerateQRConfirm] = useState(false)
  const [openGenerateQRComplete, setOpenGenerateQRComplete] = useState(false)

  const [countDownSeconds, { startCountdown }] = useCountdown({
    countStart:
      serial?.serial?.metadata?.wallpaper?.print?.expiredAt / 1000 -
      dayjs().unix(),
    intervalMs: 1000,
  })

  const isExpired = useMemo(() => {
    return (
      serial?.serial?.metadata?.wallpaper?.print?.expiredAt / 1000 <
      dayjs().unix()
    )
  }, [serial?.serial?.metadata?.wallpaper?.print?.expiredAt])

  useEffect(() => {
    if (serial?.serial?.metadata?.wallpaper?.print?.expiredAt) {
      startCountdown()
    }
  }, [serial?.serial?.metadata?.wallpaper?.print?.expiredAt])

  return (
    <Card elevation={0} sx={{ borderRadius: 2, minHeight: '276px' }}>
      <CardContent sx={{ px: 5, py: 6 }}>
        <Stack direction="row" spacing={2}>
          <ItemImageArea
            src={item?.thumbnail?.front?.mediumUri ?? ''}
            size="x-small"
          />
          <Stack>
            <Typography variant="h6">{item?.title}</Typography>
            <Typography variant="body2">
              {serial?.serial?.sku?.unitName}
            </Typography>
            {item?.metadata?.wallpaper?.printType && (
              <Typography variant="body2">
                {t(item?.metadata?.wallpaper?.printType)}
              </Typography>
            )}
          </Stack>
        </Stack>
        {serial?.serial?.metadata?.wallpaper?.print?.contentsCode && (
          <Stack pt={2}>
            <Typography variant="caption" color="text.sencondary">
              {t('serialNumber', {
                serial:
                  serial?.serial?.metadata?.wallpaper?.print?.contentsCode,
              })}
            </Typography>
          </Stack>
        )}
        {serial?.serial?.metadata?.wallpaper?.print ? (
          <Stack alignItems="center">
            {isExpired ? (
              <Typography
                variant="caption"
                color="text.sencondary"
                sx={{ p: 7 }}
              >
                {t('dateExpired')}
              </Typography>
            ) : (
              <Stack spacing={2} mt={6} alignItems="center">
                <Avatar
                  src={
                    serial?.serial?.metadata?.wallpaper?.print?.qrcode?.uri ??
                    ''
                  }
                  variant="square"
                  sx={{ width: 80, height: 80 }}
                />
                <Typography variant="subtitle2" color="text.secondary">
                  {t('expirationDate', {
                    ...convertSecondsToTime(countDownSeconds),
                  })}
                </Typography>
              </Stack>
            )}
          </Stack>
        ) : (
          <Stack mt={6} py={3} alignItems="center" justifyContent="center">
            <Button
              size="large"
              endIcon={
                <Icons.QRCode
                  fontSize="small"
                  sx={{ height: 19, width: 19, mb: '4px' }}
                />
              }
              sx={{ minWidth: 244 }}
              onClick={() => setOpenGenerateQRConfirm(true)}
            >
              {t('getQRCode')}
            </Button>
          </Stack>
        )}
      </CardContent>
      <InformationModal
        open={openGenerateQRConfirm}
        onClose={() => setOpenGenerateQRConfirm(false)}
        title={t('generateQRConfirmTitle')}
        content={t('generateQRConfirmContent')}
        cancelText={t('close')}
        buttons={
          <Button
            sx={{ width: 212 }}
            loading={loading}
            size="large"
            onClick={async () => {
              await onGenerateWallpaperPrintQrcode(serial.serialId)
                .then(() => {
                  setOpenGenerateQRComplete(true)
                })
                .catch(showError)
                .finally(() => {
                  setOpenGenerateQRConfirm(false)
                })
            }}
          >
            {t('ok')}
          </Button>
        }
      />
      <InformationModal
        open={openGenerateQRComplete}
        onClose={() => setOpenGenerateQRComplete(false)}
        title=""
        content={
          <Stack sx={{ pb: 4 }}>
            <Stack direction="row" spacing={2}>
              <ItemImageArea
                src={item?.thumbnail?.front?.mediumUri ?? ''}
                size="x-small"
              />
              <Stack alignItems="start">
                <Typography variant="h6">{item?.title}</Typography>
                <Typography variant="body2">
                  {serial?.serial?.sku?.unitName}
                </Typography>
                {item?.metadata?.wallpaper?.printType && (
                  <Typography variant="body2">
                    {t(item?.metadata?.wallpaper?.printType)}
                  </Typography>
                )}
              </Stack>
            </Stack>
            <Typography
              variant="caption"
              color="text.sencondary"
              textAlign="start"
              sx={{ width: '100%' }}
            >
              {t('serialNumber', {
                serial:
                  serial?.serial?.metadata?.wallpaper?.print?.contentsCode,
              })}
            </Typography>
            <Stack width="100%" alignItems="center">
              <Avatar
                src={
                  serial?.serial?.metadata?.wallpaper?.print?.qrcode?.uri ?? ''
                }
                variant="square"
                sx={{ width: 80, height: 80, mt: 6 }}
              />
            </Stack>

            <Stack spacing={1} mt={6} alignItems="center">
              <Typography variant="body2">
                {t('QRCodeConfirmDescription')}
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                textAlign="start"
              >
                {t('QRCodeConfirmNote')}
              </Typography>
            </Stack>
          </Stack>
        }
        cancelText={t('leavingPrint')}
      />
    </Card>
  )
}

function convertSecondsToTime(seconds: number) {
  if (seconds <= 0) {
    return { day: '00', time: '00:00:00' }
  }
  const date = new Date(seconds * 1000).toISOString()
  const day = Math.floor(seconds / (24 * 60 * 60))
    .toString()
    .padStart(2, '0')
  return { day, time: date.substring(11, 19) }
}
